import { useRefreshStudents } from '@/composables/student/useRefreshStudents';
import type { Components } from '@/api/generated/api';
import { usePermissions } from '@/composables/usePermissions';
import { useQueryWithToast } from '@/composables/useQueryWithToast';
import { useToast } from '@/composables/useToast';
import { useWhoAmI } from '@/composables/useWhoAmI';
import { schoolClassAccessKeys } from '@/composables/schoolClassAccess/schoolClassAccess.keys';
import { useCurrentClass } from '@/schoolClasses/useCurrentClass';
import { useQueryClient } from '@tanstack/vue-query';
import { computed, type ComputedRef } from 'vue';
import {
  assignStudentsToSubject as apiAssignStudentsToSubject,
  canUserAccessAllSubjectsInClass as apiCanUserAccessAllSubjectsInClass,
  copySchoolSubjectsFromTemplate,
  createSchoolSubject as apiCreateSchoolSubject,
  createSchoolSubjectTemplate as apiCreateSchoolSubjectTemplate,
  deleteSchoolSubject as apiDeleteSchoolSubject,
  duplicateSchoolSubject as apiDuplicateSchoolSubject,
  fetchAvailableSchoolSubjectsForSchoolLevel,
  fetchSchoolSubjectsForClass,
  fetchTenantSubjectTemplates,
  getStudentsWithAccessToSubject as apiGetStudentsWithAccessToSubject,
  getSubjectSpecificAssessmentSetupPreview as apiGetSubjectSpecificAssessmentSetupPreview,
  setUseSubjectSpecificAssessments as apiSetUseSubjectSpecificAssessments,
  setupSubjectSpecificAssessments as apiSetupSubjectSpecificAssessments,
  updateSchoolSubject as apiUpdateSchoolSubject,
} from '@/api/resources/schoolSubject.api';
import { useCurrentTemplateSet } from '../provider/useCurrentTemplateSet';
import { schoolSubjectKeys } from './schoolSubject.keys';
import { runBulk } from 'common/utils/function.utils';
import { invalidatePerformanceAssessmentEntryQueries } from '../performanceAssessmentEntry/performanceAssessmentEntryQueryInvalidation';
import { invalidateSubjectSpecificPerformanceAssessmentQueries } from '../performanceAssessment/subjectSpecificPerformanceAssessmentQueryInvalidation';
import { supportMeasureEntryKeys } from '../supportMeasureEntry/supportMeasureEntry.keys';
import { useSchoolSubjectGroupsQuery } from '../schoolSubjectGroups/schoolSubjectGroup.queries';
import { getOrderedSchoolSubjects } from 'common/utils/school.subject.utils';

interface ISchoolSubjectComposable {
  allSchoolSubjects: ComputedRef<Components.Schemas.SchoolSubjectDto[]>;
  allSchoolSubjectTemplates: ComputedRef<Components.Schemas.SchoolSubjectDto[]>;
  schoolSubjectsLoaded: ComputedRef<boolean>;
  areSchoolSubjectsLoading: ComputedRef<boolean>;
  areSchoolSubjectTemplatesLoading: ComputedRef<boolean>;
  canAccessAllSubjectsInClass: ComputedRef<boolean>;

  refresh: () => Promise<void>;

  createSchoolSubject: (schoolClassId: number, dto: Components.Schemas.CreateSchoolSubjectDto) => Promise<void>;
  createSchoolSubjectTemplate: (dto: Components.Schemas.CreateSchoolSubjectDto) => Promise<void>;
  updateSchoolSubject: (
    schoolSubjectId: number,
    dto: Components.Schemas.UpdateSchoolSubjectDto,
    showToast?: boolean,
  ) => Promise<void>;
  deleteSchoolSubject: (schoolSubjectId: number) => Promise<void>;

  copySchoolSubjects: (schoolClassId: number, dto: Components.Schemas.CopySchoolSubjectsDto) => Promise<void>;
  duplicateSchoolSubject: (schoolSubjectId: number, name: string) => Promise<Components.Schemas.SchoolSubjectDto>;
  assignSchoolSubjectsToClass: (schoolClassId: number, dto: Components.Schemas.CopySchoolSubjectsDto) => Promise<void>;

  getSchoolSubjectColor: (schoolSubjectId: number) => string | undefined;
  getSchoolSubjectsForClass: (classId: number) => Promise<Components.Schemas.SchoolSubjectDto[]>;
  getSchoolSubjectsForCurrentClass: () => Promise<Components.Schemas.SchoolSubjectDto[]>;
  getAvailableSchoolSubjectsForSchoolLevel: (schoolLevel: number) => Promise<Components.Schemas.SchoolSubjectDto[]>;
  getStudentsWithAccessToSubject: (schoolSubjectId: number) => Promise<Components.Schemas.BaseStudentDto[]>;

  assignStudentsToSubject: (
    schoolSubjectId: number,
    dto: Components.Schemas.AssignStudentsToSubjectDto,
  ) => Promise<void>;

  upsertSchoolSubjects: (
    schoolClassId: number,
    currentSchoolSubjects: Components.Schemas.SchoolSubjectDto[],
  ) => Promise<void>;

  setUseSubjectSpecificAssessments: (
    schoolSubjectId: number,
    dto: Components.Schemas.SetSubjectSpecificAssessmentsDto,
  ) => Promise<void>;
  getSubjectSpecificAssessmentSetupPreview: (
    schoolSubjectId: number,
  ) => Promise<Components.Schemas.SubjectSpecificAssessmentSetupPreviewDto>;
  setupSubjectSpecificAssessments: (schoolSubjectId: number) => Promise<Components.Schemas.SchoolSubjectDto>;
}

export const useSchoolSubject = (): ISchoolSubjectComposable => {
  const { createSuccessToast, editSuccessToast, deleteSuccessToast, catchError } = useToast();
  const { currentClass } = useCurrentClass();
  const { canManageCompetences, canManageStudents, hasProviderAccess } = usePermissions();
  const { currentTemplateSet } = useCurrentTemplateSet();
  const queryClient = useQueryClient();
  const { refreshStudents } = useRefreshStudents();
  const { refresh: refreshWhoAmI } = useWhoAmI();

  const classId = computed(() => currentClass.value?.id);
  const enabledClass = computed(() => Boolean(classId.value));

  const templateParams = computed(() => ({
    canManageCompetences: canManageCompetences.value,
    hasProviderAccess: hasProviderAccess.value,
    templateSetId: currentTemplateSet.value?.id ?? null,
  }));

  const classListKey = computed(() => schoolSubjectKeys.classList(classId.value));
  const templateListKey = computed(() => schoolSubjectKeys.templateList(templateParams.value));

  const getSchoolSubjectsForCurrentClass = async (): Promise<Components.Schemas.SchoolSubjectDto[]> => {
    return fetchSchoolSubjectsForClass({ classId: classId.value! });
  };

  const getAllSubjectTemplates = async (): Promise<Components.Schemas.SchoolSubjectDto[]> => {
    if (!canManageCompetences.value) return [];

    if (hasProviderAccess.value) {
      const templateSetId = currentTemplateSet.value?.id;
      if (!templateSetId) return [];
      return fetchTenantSubjectTemplates({ templateSetId });
    }
    return fetchTenantSubjectTemplates();
  };

  const classQuery = useQueryWithToast({
    queryKey: classListKey,
    enabled: enabledClass,
    queryFn: getSchoolSubjectsForCurrentClass,
  });

  const templateQuery = useQueryWithToast({
    queryKey: templateListKey,
    queryFn: getAllSubjectTemplates,
  });

  const canAccessAllKey = computed(() => schoolSubjectKeys.canAccessAll(classId.value));
  const canAccessAllQuery = useQueryWithToast({
    queryKey: canAccessAllKey,
    enabled: enabledClass,
    queryFn: () => apiCanUserAccessAllSubjectsInClass({ classId: classId.value! }),
  });

  const groupsQuery = useSchoolSubjectGroupsQuery(classId);
  const allSchoolSubjects = computed(() =>
    getOrderedSchoolSubjects(classQuery.data.value ?? [], groupsQuery.data.value ?? []),
  );
  const allSchoolSubjectTemplates = computed(() => templateQuery.data.value ?? []);
  const schoolSubjectsLoaded = computed(() => !classQuery.isLoading.value && !!classQuery.data.value);
  const areSchoolSubjectsLoading = computed(() => classQuery.isLoading.value || classQuery.isFetching.value);
  const areSchoolSubjectTemplatesLoading = computed(
    () => templateQuery.isLoading.value || templateQuery.isFetching.value,
  );
  const canAccessAllSubjectsInClass = computed(() => canAccessAllQuery.data.value ?? false);

  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: schoolSubjectKeys.lists() }),
      queryClient.invalidateQueries({ queryKey: schoolSubjectKeys.details() }),
      refreshStudents(), // refresh students to update student count in some components
      invalidatePerformanceAssessmentEntryQueries(queryClient, classId.value),
    ]);
  };

  const createSchoolSubject = async (schoolClassId: number, dto: Components.Schemas.CreateSchoolSubjectDto) => {
    await catchError(async () => {
      await apiCreateSchoolSubject({ schoolClassId, dto });
      await refresh();
      createSuccessToast();
    });
  };

  const createSchoolSubjectTemplate = async (dto: Components.Schemas.CreateSchoolSubjectDto) => {
    await catchError(async () => {
      await apiCreateSchoolSubjectTemplate(dto);
      await refresh();
      createSuccessToast();
    });
  };

  const updateSchoolSubject = async (
    schoolSubjectId: number,
    dto: Components.Schemas.UpdateSchoolSubjectDto,
    showToast?: boolean,
  ) => {
    await catchError(async () => {
      await apiUpdateSchoolSubject({ schoolSubjectId, dto });
      await refresh();
      if (showToast) editSuccessToast();
    });
  };

  const deleteSchoolSubject = async (schoolSubjectId: number) => {
    await catchError(async () => {
      await apiDeleteSchoolSubject({ schoolSubjectId });
      await refresh();
      deleteSuccessToast();
    });
  };

  const copySchoolSubjects = async (schoolClassId: number, dto: Components.Schemas.CopySchoolSubjectsDto) => {
    await copySchoolSubjectsFromTemplate({ schoolClassId, dto });
    await refresh();
  };

  const duplicateSchoolSubject = async (
    schoolSubjectId: number,
    name: string,
  ): Promise<Components.Schemas.SchoolSubjectDto> => {
    return catchError(async () => {
      const duplicate = await apiDuplicateSchoolSubject({ schoolSubjectId, dto: { name } });
      await Promise.all([
        refresh(),
        refreshWhoAmI(),
        queryClient.invalidateQueries({ queryKey: schoolClassAccessKeys.all }),
        queryClient.invalidateQueries({ queryKey: canAccessAllKey.value }),
      ]);
      createSuccessToast();
      return duplicate;
    });
  };

  const assignSchoolSubjectsToClass = async (schoolClassId: number, dto: Components.Schemas.CopySchoolSubjectsDto) => {
    await catchError(async () => {
      await copySchoolSubjects(schoolClassId, dto);
      editSuccessToast();
      await refresh();
    });
  };

  const assignStudentsToSubject = async (
    schoolSubjectId: number,
    dto: Components.Schemas.AssignStudentsToSubjectDto,
  ) => {
    if (!canManageStudents.value) return;

    await catchError(async () => {
      await apiAssignStudentsToSubject({ schoolSubjectId, dto });
      // losing subject access detaches the student from the active support measures of that subject
      await queryClient.invalidateQueries({ queryKey: supportMeasureEntryKeys.all });
      await refresh();
    });
  };

  const setUseSubjectSpecificAssessments = async (
    schoolSubjectId: number,
    dto: Components.Schemas.SetSubjectSpecificAssessmentsDto,
  ) => {
    await catchError(async () => {
      await apiSetUseSubjectSpecificAssessments({ schoolSubjectId, dto });
      await invalidateSubjectSpecificPerformanceAssessmentQueries(queryClient, classId.value);
      if (!dto.value) deleteSuccessToast();
    });
  };

  const getSubjectSpecificAssessmentSetupPreview = async (
    schoolSubjectId: number,
  ): Promise<Components.Schemas.SubjectSpecificAssessmentSetupPreviewDto> => {
    return catchError(() => apiGetSubjectSpecificAssessmentSetupPreview({ schoolSubjectId }));
  };

  const setupSubjectSpecificAssessments = async (
    schoolSubjectId: number,
  ): Promise<Components.Schemas.SchoolSubjectDto> => {
    return catchError(async () => {
      const subject = await apiSetupSubjectSpecificAssessments({ schoolSubjectId });
      await invalidateSubjectSpecificPerformanceAssessmentQueries(queryClient, classId.value);
      return subject;
    });
  };

  const getSchoolSubjectColor = (schoolSubjectId: number) =>
    allSchoolSubjects.value.find((s) => s.id === schoolSubjectId)?.color;

  const getSchoolSubjectsForClass = (id: number) => fetchSchoolSubjectsForClass({ classId: id });

  const getStudentsWithAccessToSubject = (schoolSubjectId: number) =>
    apiGetStudentsWithAccessToSubject({ schoolSubjectId });

  const getAvailableSchoolSubjectsForSchoolLevel = (schoolLevel: number) =>
    fetchAvailableSchoolSubjectsForSchoolLevel({ schoolLevel });

  const upsertSchoolSubjects = async (
    schoolClassId: number,
    currentSchoolSubjects: Components.Schemas.SchoolSubjectDto[],
  ) => {
    if (!canManageCompetences.value) return;

    const existing = allSchoolSubjects.value;

    const toDelete = existing.filter((s) => !currentSchoolSubjects.some((x) => x.id === s.id));
    const toCopy = currentSchoolSubjects.filter((s) => !existing.some((x) => x.id === s.id));

    if (!toDelete.length && !toCopy.length) return;

    await catchError(async () => {
      // delete removed
      await runBulk(toDelete, (s) => apiDeleteSchoolSubject({ schoolSubjectId: s.id }), { concurrency: 8 });

      // copy new
      if (toCopy.length > 0) {
        await copySchoolSubjectsFromTemplate({
          schoolClassId,
          dto: { schoolSubjectIdsToCopy: toCopy.map((s) => s.id) },
        });
      }

      await refresh();
    });
  };

  return {
    allSchoolSubjects,
    allSchoolSubjectTemplates,
    schoolSubjectsLoaded,
    areSchoolSubjectsLoading,
    areSchoolSubjectTemplatesLoading,
    canAccessAllSubjectsInClass,

    refresh,

    createSchoolSubject,
    createSchoolSubjectTemplate,
    updateSchoolSubject,
    deleteSchoolSubject,

    copySchoolSubjects,
    duplicateSchoolSubject,
    assignSchoolSubjectsToClass,

    getSchoolSubjectColor,
    getSchoolSubjectsForClass,
    getSchoolSubjectsForCurrentClass,
    getAvailableSchoolSubjectsForSchoolLevel,
    getStudentsWithAccessToSubject,

    assignStudentsToSubject,
    upsertSchoolSubjects,
    setUseSubjectSpecificAssessments,
    getSubjectSpecificAssessmentSetupPreview,
    setupSubjectSpecificAssessments,
  };
};
